import React, { useState, useEffect, useCallback } from "react";
import {
  Badge,
  IconButton,
  Menu,
  Typography,
  Divider,
  Box,
  useMediaQuery,
  useTheme,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Button,
  CircularProgress,
} from "@mui/material";
import { Helper } from "../../tools/Helper";
import NotificationsIcon from "@mui/icons-material/Notifications";
import CloseIcon from "@mui/icons-material/Close";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import pusher from "../../helper/pusher";
import { api_Routes } from "../../tools/api_Routes";
import { useNotifications } from "../../Context/notificationContext";

const NotificationBell = () => {
  const { state, dispatch } = useNotifications();
  const theme = useTheme();
  const userData = JSON.parse(localStorage.getItem("user"));
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotificationsState] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isMarkingAll, setIsMarkingAll] = useState(false);

  const countUnreadNotifications = useCallback(
    (notifs) => notifs.filter((n) => !n.is_read).length,
    []
  );

  const getNotifications = useCallback(async () => {
    setIsLoading(true);
    try {
      const { response } = await Helper.Get({
        url: `${api_Routes.Notifications.View}`,
        hasToken: true,
      });
      if (response && Array.isArray(response)) {
        setNotificationsState(response);
        const unreadCount = countUnreadNotifications(response);
        dispatch({ type: "SET_NOTIFICATIONS", payload: unreadCount });
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, countUnreadNotifications]);

  const handleMarkAllAsRead = async () => {
    if (countUnreadNotifications(notifications) === 0) return;

    setIsMarkingAll(true);
    try {
      const { response } = await Helper.Get({
        url: `${api_Routes.Notifications.ReadAll}`,
        hasToken: true,
      });

      if (response) {
        const updated = notifications.map((n) => ({ ...n, is_read: true }));
        setNotificationsState(updated);
        dispatch({ type: "SET_NOTIFICATIONS", payload: 0 });
      }
    } catch (error) {
      console.error("Error marking all as read:", error);
    } finally {
      setIsMarkingAll(false);
    }
  };

  useEffect(() => {
    getNotifications();

    const handleNotification = (data) => {
      try {
        const notificationData =
          typeof data === "string" ? { message: data } : data;

        const newNoti = {
          id: notificationData.id || Date.now(),
          message: notificationData.message || "New notification",
          is_read: false,
          formatted_date: notificationData.date || new Date().toISOString(),
        };

        setNotificationsState((prev) => [newNoti, ...prev]);
        dispatch({ type: "ADD_NOTIFICATION" });
      } catch (error) {
        console.error("Error processing notification:", error);
      }
    };

    const channel = pusher.subscribe(`admin-notification.${userData.id}`);
    channel.bind("AdminNotification", handleNotification);

    return () => {
      channel.unbind("AdminNotification", handleNotification);
      pusher.unsubscribe(`admin-notification.${userData.id}`);
    };
  }, [dispatch, getNotifications, userData.id]);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleClickNoti = async (noti) => {
    if (!noti.is_read) {
      try {
        const { response } = await Helper.Get({
          url: `${api_Routes.Notifications.Read}/${noti.id}`,
          hasToken: true,
        });

        if (response) {
          setNotificationsState((prev) =>
            prev.map((n) => (n.id === noti.id ? { ...n, is_read: true } : n))
          );
          dispatch({
            type: "SET_NOTIFICATIONS",
            payload: countUnreadNotifications(notifications) - 1,
          });
        }
      } catch (error) {
        console.error("Error marking notification as read:", error);
      }
    }
    if (noti.description?.link) {
      window.location.href = noti.description.link;
    } else if (noti.message?.description?.link) {
      window.location.href = noti.message?.description?.link;
    }
  };

  return (
    <Box>
      <IconButton
        color="inherit"
        onClick={handleMenuOpen}
        aria-label="notifications"
        size={isMobile ? "small" : "medium"}
      >
        <Badge
          badgeContent={state.newNotifications}
          color="error"
          overlap="circular"
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <NotificationsIcon fontSize={isMobile ? "small" : "medium"} />
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            width: isMobile ? "90vw" : 400,
            maxHeight: "70vh",
            borderRadius: "12px",
            boxShadow: theme.shadows[4],
          },
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        MenuListProps={{
          sx: { p: 0 },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 2,
            bgcolor: "primary.main",
            color: "primary.contrastText",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderRadius: "12px 12px 0 0",
          }}
        >
          <Typography variant="h6">Notifications</Typography>
          <IconButton
            onClick={handleMenuClose}
            size="small"
            sx={{ color: "inherit" }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Notification List */}
        <Box sx={{ maxHeight: "50vh", overflow: "auto" }}>
          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : notifications.length === 0 ? (
            <Box sx={{ p: 2, textAlign: "center" }}>
              <Typography variant="body2" color="textSecondary">
                No new notifications
              </Typography>
            </Box>
          ) : (
            <List dense>
              {notifications.map((notification) => (
                <ListItem
                  key={notification.id}
                  button
                  onClick={() => handleClickNoti(notification)}
                  sx={{
                    px: 2,
                    py: 1,
                    "&:hover": {
                      backgroundColor: theme.palette.action.hover,
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    {!notification.is_read && (
                      <FiberManualRecordIcon
                        fontSize="small"
                        color="primary"
                        sx={{ width: 8, height: 8 }}
                      />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: notification.is_read ? 400 : 600 }}
                      >
                        {notification.message?.description?.text ??
                          notification?.description?.text}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="caption" color="textSecondary">
                        {new Date(notification.formatted_date).toLocaleString()}
                      </Typography>
                    }
                    sx={{ my: 0 }}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Box>

        {/* Footer */}
        <Box
          sx={{
            p: 1.5,
            borderTop: `1px solid ${theme.palette.divider}`,
            display: "flex",
            gap: 1,
            justifyContent: "space-between",
          }}
        >
          <Button
            variant="outlined"
            size="small"
            onClick={handleMarkAllAsRead}
            disabled={
              countUnreadNotifications(notifications) === 0 || isMarkingAll
            }
            startIcon={
              isMarkingAll ? <CircularProgress size={14} /> : <DoneAllIcon />
            }
            sx={{ flex: 1, textTransform: "none" }}
          >
            {isMarkingAll ? "Marking..." : "Mark All Read"}
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleMenuClose}
            sx={{ flex: 1, textTransform: "none" }}
          >
            Close
          </Button>
        </Box>
      </Menu>
    </Box>
  );
};

export default NotificationBell;
